// Row catalogue for the Usage & limits table (ENG2-2461 / T8).
//
// Order and grouping are what Sales and CS see; keep the labels close to the
// old field names so the screen stays recognisable (Filter Exports → Filter
// exports, Monitoring Count → Monitored companies, Max Users → Users,
// Lookups → Nordic lookups).

import { UntrackedReason, UsageGroup, UsageRowDefinition } from './usage-model'

export const USAGE_GROUPS: { key: UsageGroup; title: string }[] = [
  { key: 'lookups', title: 'Lookups (unique companies)' },
  { key: 'monthly', title: 'Monthly allowances' },
  { key: 'capacity', title: 'Account capacity' },
  { key: 'limit', title: 'Per-export limit' },
]

// Why a row says "Not tracked" — shown as a tooltip on the badge.
export const UNTRACKED_REASONS: Record<UntrackedReason, string> = {
  never_counted:
    'Nothing records report downloads in the platform today, so usage cannot be shown. The cap is still saved.',
  counter_out_of_range:
    'The counter behind this row was changed outside the normal flow (it no longer matches the value RisikaAPI seeds it with), so usage cannot be derived from it.',
  counter_missing:
    'RisikaAPI did not return a counter for this row, so usage cannot be shown.',
}

export const LEGEND =
  'Cap = what the plan allows · Used = consumed this period · Remaining = Cap − Used'

export const USAGE_ROWS: UsageRowDefinition[] = [
  {
    key: 'lookups',
    label: 'Nordic lookups',
    group: 'lookups',
    usage: 'nordic',
    tooltip:
      'Unique Nordic companies the customer may look up per lookup period. Opening a company page, a credit rating, a comprehensive call or adding a company to monitoring counts once per company. Until lookup metering is released, Used shows requests since the last reset instead.',
  },
  {
    key: 'search_exports',
    label: 'Filter exports',
    group: 'monthly',
    usage: 'remaining',
    tooltip:
      'How many times the customer can export a list from filtering this month, regardless of list size.',
  },
  {
    key: 'monitoring_exports',
    label: 'Monitoring exports',
    group: 'monthly',
    usage: 'remaining',
    tooltip:
      'How many times the customer can export a monitoring list this month, regardless of list size.',
  },
  {
    key: 'credit_reports',
    label: 'Credit reports',
    group: 'monthly',
    usage: 'untracked',
    tooltip:
      'Reports the customer may download per month. Nothing decrements this counter today, so usage cannot be shown.',
  },
  {
    key: 'credit_ratings',
    label: 'Credit ratings',
    group: 'monthly',
    usage: 'seeded',
    hideForActivePlans: true,
    tooltip:
      'Legacy counter: credit rating requests (recommended credit limit and days) since the last reset. For the 2026 packages credit ratings are part of the Nordic lookup pool and this row is hidden.',
  },
  {
    key: 'monitoring_count',
    label: 'Monitored companies',
    group: 'capacity',
    usage: 'used',
    tooltip:
      'Companies currently on the customer’s monitoring lists, against the cap.',
  },
  {
    key: 'max_users',
    label: 'Users',
    group: 'capacity',
    usage: 'users',
    tooltip:
      'Users on the account, against the maximum number of users allowed.',
  },
  {
    key: 'search_exports_limit',
    label: 'Rows per filter export',
    group: 'limit',
    usage: 'none',
    tooltip:
      'Maximum number of rows in a single filter export. A size limit, not an allowance.',
  },
]
